import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Empty, ErrorNote, FlashNote, Loading, Note, PageHead, Pill, PlaceholderBadge, useFlash, VehicleTile } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { day, rupees, rupeesExact, VEHICLE_LABEL } from '../lib/format';
import { useFares, useStaffNames } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import { VEHICLE_TYPES, type FareConfig, type FareInput, type VehicleType } from '../lib/types';

/** Same formula as the server (backend/src/fares/fare-calculator.ts), for the preview only. */
export function estimateFare(r: FareInput, meters: number, seconds: number): number {
  const km = Math.ceil(meters / 100) / 10;
  const min = Math.ceil(seconds / 60);
  const trip = r.baseFarePaise + Math.round(r.perKmPaise * km) + r.perMinPaise * min;
  const sub = Math.max(trip, r.minimumFarePaise) + r.bookingFeePaise;
  return Math.floor((sub + 50) / 100) * 100;
}

const FIELDS: { key: keyof FareInput; label: string; hint: string }[] = [
  { key: 'baseFarePaise', label: 'Base fare', hint: 'Charged on every ride' },
  { key: 'perKmPaise', label: 'Per km', hint: 'Per started 100 m' },
  { key: 'perMinPaise', label: 'Per minute', hint: 'Per started minute; 0 for distance only' },
  { key: 'minimumFarePaise', label: 'Minimum fare', hint: 'Not lower than the base fare' },
  { key: 'bookingFeePaise', label: 'Booking fee', hint: 'Added on top; 0 for none' },
];

const toRupeeText = (p: number) => String(p / 100);
const toPaise = (s: string) => {
  const n = Number(s.replace(/[,\s₹]/g, ''));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN;
};

const EXAMPLES = [
  { label: '3 km · 10 min', m: 3000, s: 600 },
  { label: '10 km · 25 min', m: 10000, s: 1500 },
  { label: 'Jourian → Akhnoor · 21.7 km · 55 min', m: 21696, s: 3254 },
];

function FareEditor({ current, onDone }: { current: FareConfig; onDone: (published?: string) => void }) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<keyof FareInput, string>>(() => {
    const v = {} as Record<keyof FareInput, string>;
    for (const f of FIELDS) v[f.key] = toRupeeText(current[f.key]);
    return v;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const parsed = Object.fromEntries(FIELDS.map((f) => [f.key, toPaise(values[f.key])])) as FareInput;
  const invalid = FIELDS.some((f) => Number.isNaN(parsed[f.key]));
  const minTooLow = !invalid && parsed.minimumFarePaise < parsed.baseFarePaise;

  return (
    <form
      className="stack-sm"
      style={{ gap: 18 }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (invalid || minTooLow) return;
        setBusy(true);
        setError(null);
        try {
          await api.put<FareConfig>(`/admin/fares/${current.vehicleType}`, parsed);
          await qc.invalidateQueries({ queryKey: qk.fares });
          onDone(`${VEHICLE_LABEL[current.vehicleType]} fare published as version ${current.version + 1}. New prices use it now.`);
        } catch (err) {
          setError(errorMessage(err));
          setBusy(false);
        }
      }}
    >
      <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
        {FIELDS.map((f) => (
          <label key={f.key} className="field">
            <span className="field-label">{f.label}</span>
            <span className="input-affix">
              <span className="prefix">₹</span>
              <input
                className="input has-prefix num"
                inputMode="decimal"
                value={values[f.key]}
                aria-label={`${f.label} in rupees`}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              />
            </span>
            <span className="field-hint">{f.hint}</span>
          </label>
        ))}
      </div>
      {!invalid && (
        <div className="stack-xs">
          <span className="overline">Examples with these numbers</span>
          <Examples r={parsed} />
        </div>
      )}
      {minTooLow && <p className="error-text">The minimum fare can’t be lower than the base fare.</p>}
      {invalid && <p className="error-text">Use numbers only, like 30 or 1.50.</p>}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={() => onDone()}>
          Cancel
        </button>
        <button className="btn btn-dark" disabled={busy || invalid || minTooLow}>
          {busy ? 'Publishing…' : 'Publish new version'}
        </button>
      </div>
      <p className="small faint">
        Publishing creates version {current.version + 1}. Rides already booked, and prices riders were already shown, keep
        their fare.
      </p>
    </form>
  );
}

function Examples({ r }: { r: FareInput }) {
  return (
    <div className="examples">
      {EXAMPLES.map((x) => (
        <div key={x.label} className="example">
          <div className="small muted truncate">{x.label}</div>
          <div className="num">{rupees(estimateFare(r, x.m, x.s))}</div>
        </div>
      ))}
    </div>
  );
}

function FareCard({ type, versions, canEdit, onFlash }: { type: VehicleType; versions: FareConfig[]; canEdit: boolean; onFlash: (t: string) => void }) {
  const [editing, setEditing] = useState(false);
  const staffName = useStaffNames();
  const active = versions.find((v) => v.isActive) ?? versions[0];
  const older = versions.filter((v) => v.id !== active?.id);
  if (!active)
    return (
      <section className="card card-pad stack-sm">
        <div className="person">
          <VehicleTile type={type} />
          <h2>{VEHICLE_LABEL[type]}</h2>
        </div>
        <p className="muted">No fare yet — riders can’t book this vehicle type.</p>
      </section>
    );
  return (
    <section className="card" aria-label={`${VEHICLE_LABEL[type]} fare`} data-testid={`fare-${type}`}>
      <div className="card-head">
        <div className="person">
          <VehicleTile type={type} />
          <div>
            <h2>{VEHICLE_LABEL[type]}</h2>
            <span className="small muted">Version {active.version}</span>
          </div>
        </div>
        {active.isPlaceholder && <PlaceholderBadge />}
      </div>
      <div className="card-body">
        {editing ? (
          <FareEditor
            current={active}
            onDone={(text) => {
              setEditing(false);
              if (text) onFlash(text);
            }}
          />
        ) : (
          <div className="stack-sm" style={{ gap: 16 }}>
            <div className="row" style={{ alignItems: 'baseline', gap: 8 }}>
              <span className="fare-figure">{rupeesExact(active.baseFarePaise)}</span>
              <span className="muted strong">+ {rupeesExact(active.perKmPaise)}/km</span>
            </div>
            <dl className="kv">
              {FIELDS.map((f) => (
                <div key={f.key} style={{ display: 'contents' }}>
                  <dt>{f.label}</dt>
                  <dd className="num">{rupeesExact(active[f.key])}</dd>
                </div>
              ))}
            </dl>
            <Examples r={active} />
            <div className="spread">
              <p className="small faint">
                Since {day(active.createdAt)}
                {active.createdByAdminId ? ` · by ${staffName(active.createdByAdminId)}` : ' · set at launch'}
              </p>
              {canEdit && (
                <button className="btn btn-sm" onClick={() => setEditing(true)}>
                  Change fare
                </button>
              )}
            </div>
          </div>
        )}
        {older.length > 0 && (
          <details className="history" style={{ marginTop: 14 }}>
            <summary>Earlier versions ({older.length})</summary>
            <div className="stack-sm" style={{ marginTop: 8 }}>
              {older.map((v) => (
                <div key={v.id} className="small muted">
                  <strong style={{ color: 'var(--text)' }}>v{v.version}</strong> · {day(v.createdAt)} · base {rupeesExact(v.baseFarePaise)},{' '}
                  {rupeesExact(v.perKmPaise)}/km, {rupeesExact(v.perMinPaise)}/min, min {rupeesExact(v.minimumFarePaise)}
                  {v.bookingFeePaise ? `, fee ${rupeesExact(v.bookingFeePaise)}` : ''}
                  {v.isPlaceholder ? ' · placeholder' : ''}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </section>
  );
}

export function FaresPage() {
  const { isOwner } = useAuth();
  const q = useFares();
  const [flash, show] = useFlash();
  const anyPlaceholder = q.data?.some((f) => f.isActive && f.isPlaceholder);
  return (
    <div className="stack">
      <PageHead
        title="Fares"
        sub="The fare a rider sees before booking is the fare they pay."
        actions={!isOwner && <Pill>View only</Pill>}
      />
      <FlashNote flash={flash} />
      {!isOwner && <Note testId="owner-only-note">Only the owner can change fares. You can see every version here.</Note>}
      {anyPlaceholder && (
        <Note tone="warn">Fares marked Placeholder haven’t been approved by the owner yet. Riders may see them as introductory fares.</Note>
      )}
      {q.isLoading ? (
        <Loading rows={6} />
      ) : q.error ? (
        <ErrorNote error={q.error} />
      ) : !q.data?.length ? (
        <Empty title="No fares" />
      ) : (
        <div className="grid-3">
          {VEHICLE_TYPES.map((t) => (
            <FareCard key={t} type={t} versions={q.data.filter((f) => f.vehicleType === t)} canEdit={isOwner} onFlash={(x) => show(x)} />
          ))}
        </div>
      )}
    </div>
  );
}
